/**
 * Funzioni di utilità generiche
 */

/**
 * Decodifica un JWT senza verificarne la firma (solo parsing)
 * @param {string} token - JWT token
 * @returns {object|null} Payload decodificato o null se invalido
 */
export const decodeJWT = (token) => {
  if (!token) return null;

  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Errore decodifica JWT:', error);
    return null;
  }
};

/**
 * Verifica se un token JWT è scaduto
 * @param {string} token - JWT token
 * @returns {boolean}
 */
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const expiryTime = decoded.exp * 1000; // Converti in millisecondi
  return Date.now() >= expiryTime;
};

/**
 * Ottiene il tempo rimanente prima della scadenza del token (in millisecondi)
 * @param {string} token - JWT token
 * @returns {number} Millisecondi rimanenti, 0 se scaduto
 */
export const getTokenTimeRemaining = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return 0;

  const expiryTime = decoded.exp * 1000;
  const remaining = expiryTime - Date.now();

  return remaining > 0 ? remaining : 0;
};

/**
 * Genera le iniziali da un nome completo
 * @param {string} fullName - Nome completo (es. "Mario Rossi")
 * @returns {string} Iniziali (es. "MR")
 */
export const getInitials = (fullName) => {
  if (!fullName) return '?';

  const names = fullName.trim().split(' ');
  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  }

  return (
    names[0].charAt(0).toUpperCase() + names[names.length - 1].charAt(0).toUpperCase()
  );
};

/**
 * Formatta una data ISO in formato leggibile italiano
 * @param {string} isoDate - Data in formato ISO
 * @returns {string} Data formattata (es. "19/11/2025 10:30")
 */
export const formatDate = (isoDate) => {
  if (!isoDate) return '';

  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Costruisce l'URL completo per un'immagine dal percorso relativo
 * @param {string} imagePath - Percorso relativo dell'immagine (es. "/35/image.png")
 * @returns {string} URL completo
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:61473';
  const imagesBasePath = '/Data/Images/img';

  // Se il percorso inizia già con http/https, restituiscilo così com'è
  if (imagePath.startsWith('http')) return imagePath;

  // Altrimenti costruisci l'URL completo: baseUrl + /Data/Images/img + imagePath
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseUrl}${imagesBasePath}${cleanPath}`;
};

/**
 * Valida un indirizzo email
 * @param {string} email - Email da validare
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida una password (almeno 8 caratteri, 1 maiuscola, 1 numero, 1 carattere speciale)
 * @param {string} password - Password da validare
 * @returns {boolean}
 */
export const isValidPassword = (password) => {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
};

/**
 * Determina il tipo di messaggio (success, warning, error) in base al codice HTTP
 * @param {number} code - Codice HTTP
 * @param {boolean} success - Flag success dalla response
 * @returns {string} Tipo: 'success' | 'warning' | 'error'
 */
export const getMessageType = (code, success) => {
  if (success && code >= 200 && code < 300) return 'success';
  if (code >= 400 && code < 500) return 'warning';
  if (code >= 500) return 'error';
  return 'info';
};

/**
 * Ritardo asincrono (helper per testing/animazioni)
 * @param {number} ms - Millisecondi di attesa
 * @returns {Promise}
 */
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Divide un nome completo in nome e cognome, gestendo prefissi italiani comuni
 * @param {string} fullName - Nome completo (es. "Mario Del Piero")
 * @returns {Object} { nome: string, cognome: string }
 */
export const splitNomeCognome = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return { nome: '', cognome: '' };
  }

  const trimmed = fullName.trim();
  if (!trimmed) {
    return { nome: '', cognome: '' };
  }

  // Prefissi comuni dei cognomi italiani (case-insensitive)
  const prefissi = [
    'del', 'della', 'delle', 'dello', 'dei', 'degli',
    'di', 'da', 'de', 'd\'',
    'van', 'von', 'von der', 'van den', 'van der',
    'la', 'lo', 'le', 'li',
    'san', 'sant', 'santa', 'santo'
  ];

  const parts = trimmed.split(/\s+/);

  // Se c'è solo una parola, consideriamola come cognome
  if (parts.length === 1) {
    return { nome: '', cognome: parts[0] };
  }

  // Se ci sono due parole senza prefissi: COGNOME NOME (formato italiano)
  if (parts.length === 2) {
    return { nome: parts[1], cognome: parts[0] };
  }

  // Se ci sono 3+ parole, cerca prefissi nel cognome (che viene prima)
  // Verifica se la prima parola è un prefisso
  const primaParola = parts[0].toLowerCase().replace(/[''']/g, '\'');
  const isPrefisso = prefissi.some(p => primaParola === p || primaParola === p.replace(/[''']/g, '\''));

  if (isPrefisso && parts.length >= 3) {
    // Il cognome include il prefisso e la parola successiva
    // Es: "Del Piero Alessandro" -> cognome: "Del Piero", nome: "Alessandro"
    const cognome = parts.slice(0, 2).join(' ');
    const nome = parts.slice(2).join(' ');
    return { nome, cognome };
  }

  // Se non c'è un prefisso riconosciuto: prima parola è cognome, resto è nome
  // Es: "Rossi Mario Giuseppe" -> cognome: "Rossi", nome: "Mario Giuseppe"
  const cognome = parts[0];
  const nome = parts.slice(1).join(' ');
  return { nome, cognome };
};

/**
 * Valida il formato del codice fiscale italiano
 * @param {string} codiceFiscale - Codice fiscale da validare
 * @returns {Object} { valid: boolean, message: string }
 */
export const validateCodiceFiscale = (codiceFiscale) => {
  if (!codiceFiscale || typeof codiceFiscale !== 'string') {
    return { valid: false, message: 'Il codice fiscale è obbligatorio' };
  }

  const cf = codiceFiscale.trim().toUpperCase();

  // Verifica lunghezza
  if (cf.length !== 16) {
    return { valid: false, message: 'Il codice fiscale deve essere di 16 caratteri' };
  }

  // Pattern: 6 lettere + 2 numeri + 1 lettera + 2 numeri + 1 lettera + 3 numeri + 1 lettera
  const cfRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;

  if (!cfRegex.test(cf)) {
    return { valid: false, message: 'Il formato del codice fiscale non è valido' };
  }

  // TODO: Riabilitare la validazione stretta del carattere di controllo a progetto finito
  // TEMPORANEO: Validazione checksum disabilitata per permettere test con codici fiscali fittizi
  // Questo permette di testare la funzionalità senza dover usare codici fiscali reali validi

  /* VALIDAZIONE CHECKSUM DISABILITATA TEMPORANEAMENTE
  // Verifica checksum (carattere di controllo) secondo l'algoritmo ufficiale
  // Valori per caratteri in posizione dispari (0-indexed: 0, 2, 4, 6, 8, 10, 12, 14)
  const valoriDispari = {
    '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
    'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
    'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
    'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
  };

  // Valori per caratteri in posizione pari (0-indexed: 1, 3, 5, 7, 9, 11, 13)
  const valoriPari = {
    '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
    'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
    'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
    'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
  };

  const carattereControllo = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  let somma = 0;

  // Calcola somma per caratteri in posizione dispari (0-indexed: 0, 2, 4, ...)
  for (let i = 0; i < 15; i += 2) {
    const char = cf[i];
    somma += valoriDispari[char] || 0;
  }

  // Calcola somma per caratteri in posizione pari (0-indexed: 1, 3, 5, ...)
  for (let i = 1; i < 15; i += 2) {
    const char = cf[i];
    somma += valoriPari[char] || 0;
  }

  const resto = somma % 26;
  const expectedCheckChar = carattereControllo[resto];
  const actualCheckChar = cf[15];

  if (actualCheckChar !== expectedCheckChar) {
    return { valid: false, message: 'Il carattere di controllo del codice fiscale non è corretto' };
  }
  */

  return { valid: true, message: '' };
};

/**
 * Decodifica HTML entities in una stringa
 * Converte &#039; → ', &agrave; → à, &lt; → <, ecc.
 * @param {string} html - Stringa con HTML entities
 * @returns {string} Stringa decodificata
 */
export const decodeHtmlEntities = (html) => {
  if (!html || typeof html !== 'string') return '';

  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};
